import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import type { TerminalProvider } from '@/lib/paymentLauncher';
import type { Json } from '@/integrations/supabase/types';

export interface PendingExternalPayment {
  id: string;
  company_id: string;
  sale_id: string | null;
  transaction_id: string;
  amount: number;
  payment_method: 'credit' | 'debit';
  provider: TerminalProvider;
  installments: number;
  status: 'pending' | 'processing' | 'success' | 'failed' | 'cancelled' | 'timeout';
  launched_at: string;
  completed_at: string | null;
  return_data: Json | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreatePendingPaymentParams {
  sale_id?: string;
  transaction_id: string;
  amount: number;
  payment_method: 'credit' | 'debit';
  provider: TerminalProvider;
  installments?: number;
}

export interface UpdatePendingPaymentParams {
  transaction_id: string;
  status: PendingExternalPayment['status'];
  return_data?: Record<string, unknown>;
  error_message?: string;
}

export function usePendingPayments(saleId?: string) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  // Fetch pending payments for a sale
  const { data: pendingPayments, isLoading } = useQuery({
    queryKey: ['pending-payments', saleId],
    queryFn: async () => {
      let query = supabase
        .from('pending_external_payments')
        .select('*')
        .order('created_at', { ascending: false });

      if (saleId) {
        query = query.eq('sale_id', saleId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as PendingExternalPayment[];
    },
    enabled: !!profile?.company_id,
  });

  // Create a new pending payment
  const createPendingPayment = useMutation({
    mutationFn: async (params: CreatePendingPaymentParams) => {
      if (!profile?.company_id) throw new Error('Company ID not found');

      const { data, error } = await supabase
        .from('pending_external_payments')
        .insert({
          company_id: profile.company_id,
          sale_id: params.sale_id || null,
          transaction_id: params.transaction_id,
          amount: params.amount,
          payment_method: params.payment_method,
          provider: params.provider,
          installments: params.installments || 1,
          status: 'pending',
        })
        .select()
        .single();

      if (error) throw error;
      return data as PendingExternalPayment;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-payments'] });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao registrar pagamento',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Update pending payment status
  const updatePendingPayment = useMutation({
    mutationFn: async (params: UpdatePendingPaymentParams) => {
      const updates: {
        status: string;
        updated_at: string;
        completed_at?: string;
        return_data?: Json;
        error_message?: string;
      } = {
        status: params.status,
        updated_at: new Date().toISOString(),
      };

      if (params.status === 'success' || params.status === 'failed' || params.status === 'cancelled') {
        updates.completed_at = new Date().toISOString();
      }

      if (params.return_data) {
        updates.return_data = params.return_data as Json;
      }

      if (params.error_message) {
        updates.error_message = params.error_message;
      }

      const { data, error } = await supabase
        .from('pending_external_payments')
        .update(updates)
        .eq('transaction_id', params.transaction_id)
        .select()
        .single();

      if (error) throw error;
      return data as PendingExternalPayment;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['pending-payments'] });
      if (data.sale_id) {
        queryClient.invalidateQueries({ queryKey: ['sales'] });
        queryClient.invalidateQueries({ queryKey: ['sale-payments', data.sale_id] });
      }
    },
  });

  // Find pending payment by transaction ID
  const findByTransactionId = async (transactionId: string): Promise<PendingExternalPayment | null> => {
    const { data, error } = await supabase
      .from('pending_external_payments')
      .select('*')
      .eq('transaction_id', transactionId)
      .single();

    if (error) return null;
    return data as PendingExternalPayment;
  };

  return {
    pendingPayments,
    isLoading,
    createPendingPayment,
    updatePendingPayment,
    findByTransactionId,
  };
}
